// dh-form-preview-type-1 — DocHub form-landing hero ("intro").
// Dark full-height stage with a dimmed, backend-generated form preview behind a
// centered heading + CTA, breadcrumbs on top and an optional footer bar.
// Colours/dimensions taken from the original DocHub page; values without a design
// token are kept raw for fidelity. Breakpoints map to the dochub theme mixins
// (dh-sm = 768px, dh-md = 1024px) ≈ the original 768/960.

$stage-bg: #404043;

.dh-form-preview-type-1 {
  position: relative;
  display: block;
  overflow: hidden;
  background-color: $stage-bg;
  color: var(--color-neutral-white, #fff);

  // Fill the viewport below the header. Uses a STATIC offset = the dh-navbar-type-1
  // height (72px, fixed at all breakpoints) so the correct height is reserved at SSR
  // and there is no client-side re-measure / layout shift (CLS). Override via
  // `--_header-offset` per page if a taller header/banner sits above the section.
  min-height: calc(100dvh - var(--_header-offset, 72px));

  // Width + horizontal gutters come from `dh-section--container` on this element.
  &__inner {
    position: relative;
    height: 100%;
    min-height: inherit;
  }

  // ── Breadcrumbs (top, inverse) ─────────────────────────────────
  // dh-breadcrumbs-type-1 is colour: inherit, so the white stage colour makes it
  // render light automatically.
  &__breadcrumbs {
    position: relative;
    z-index: 5;
    padding-top: 16px;
  }

  // ── Dimmed form preview (backdrop) ─────────────────────────────
  // Preview container widths/positions mirror the original `.form-preview`:
  // mobile ≤ 450px; from 768px it spans most of the stage; from 960px it caps at 744px.
  &__preview {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 1;
    width: 100%;
    max-width: 450px;

    // Mobile/tablet: the preview fits within the stage, so just centre it vertically.
    transform: translate(-50%, -50%);

    @media (768px <= width < 960px) {
      min-width: 80%;
      max-width: 100%;
    }

    // Desktop: the backend preview is portrait and, at this width, taller than the
    // stage. Centering it would crop the form's top+bottom and shrinking it would
    // waste space, so anchor it near the top like the original — the form content
    // sits in the upper part and the blank bottom margin overflows below the fold
    // (clipped by the stage's `overflow: hidden`). `min-width: 80%` (deliberately
    // above the 744px cap; min beats max in CSS) matches the original's wider preview
    // so the form fills the stage instead of sitting small with dead space beneath it.
    @media (width >= 960px) {
      top: 0;
      min-width: 80%;
      max-width: 744px;
      padding-top: 96px;
      transform: translateX(-50%);
    }

    // The darkening matches the original: the bright preview is blended into the
    // dark stage (#404043) purely via multiply — white areas drop to ~stage
    // colour and the edges dissolve into the background, keeping the centered copy
    // legible. No extra rgba overlay (that double-darkened it).
    mix-blend-mode: multiply;
  }

  &__image {
    position: relative;
    z-index: 1;
    display: block;

    // The image scales to the container width and its height follows the natural ratio
    // (`width:100%; height:auto`). The <img>'s width/height attributes reserve the
    // aspect-ratio box BEFORE the bytes load, so the preview never jumps on load.
    .dh-picture,
    .dh-picture picture {
      display: block;
      width: 100%;
    }

    .dh-picture img {
      width: 100%;
      height: auto;
    }
  }

  // ── Centered content (heading + CTA) ───────────────────────────
  &__content {
    position: absolute;
    top: 48%;
    left: 50%;
    z-index: 4;
    width: 100%;
    padding-inline: 24px;
    text-align: center;
    transform: translate(-50%, -50%);

    @include dh-md {
      top: 50%;
      width: 70%;
      padding-inline: 0;
    }
  }

  // White heading, clamped to 3 lines (titles can be long for SEO). Targets the
  // slotted dh-heading's rendered element without touching the shared element.
  &__heading {
    margin-bottom: 24px;

    @include dh-md {
      margin-bottom: 40px;
    }

    .dh-heading {
      display: -webkit-box;
      overflow: hidden;
      color: var(--color-neutral-white, #fff);
      text-overflow: ellipsis;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }
  }

  &__cta {
    display: flex;
    justify-content: center;

    // Get Form trigger: fixed min width and bold label (matches the original CTA).
    .dh-button {
      min-width: 194px;
      font-weight: 700;

      // Stretch the CTA's hit area over the whole centered block (heading + button)
      // so a click anywhere on `__content` follows the link — pure CSS, no extra
      // props or markup. `__content` is already positioned (absolute), so it is the
      // overlay's containing block; the link must be `static` (it is `position:
      // relative` by default) for the overlay to size to the block, not to itself.
      position: static;

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 1;
      }
    }
  }

  // ── Footer (details toggle + rating + collapsible description) ──
  &__footer {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 4;
    display: flex;
    flex-direction: column;
    max-width: $dh-container-max;
    margin: 0 auto;
    padding-inline: var(--spacing-lg);
    font-size: 12px;
    line-height: 16px;

    @include dh-sm {
      padding-inline: var(--spacing-3xl);
    }
  }

  // Footer bar at the very bottom. Mobile (matches the original): a centered column
  // with the rating on top and "See details" below. Desktop: a row with "See details"
  // on the left and the rating on the right (the `order` swaps live on each child).
  &__footer-bar {
    order: 2;
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    justify-content: center;
    gap: 8px 24px;
    padding: 16px 0 6px;

    @include dh-sm {
      flex-direction: row;
      justify-content: space-between;
    }
  }

  // ── "See details / Hide details" toggle ────────────────────────
  // Mobile: below the rating (order 1). Desktop: on the left (order 0).
  &__details-trigger {
    order: 1;
    display: inline-flex;
    align-items: center;
    padding: 0;
    color: inherit;
    font: inherit;
    font-weight: 700;
    background: none;
    border: none;
    cursor: pointer;

    @include dh-sm {
      order: 0;
    }

    &::after {
      content: '';
      width: 12px;
      height: 12px;
      margin-left: 4px;
      background-color: $dh-color-brand-primary;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='6' height='3' fill='none'%3E%3Cpath fill='%23fff' d='M1.354.146a.5.5 0 1 0-.708.708zM3 2.5l-.354.354a.5.5 0 0 0 .708 0zM5.354.854a.5.5 0 1 0-.708-.708zm-4.708 0 2 2 .708-.708-2-2zm2.708 2 2-2-.708-.708-2 2z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: 50%;
      border-radius: 4px;
      transition: transform 0.4s;
    }

    &--open::after {
      transform: rotate(-180deg);
    }
  }

  // ── Rating (stars + value + votes) ─────────────────────────────
  // Mobile: on top of the bar (order 0). Desktop: on the right (order 1).
  &__rating {
    order: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;

    @include dh-sm {
      order: 1;
    }
  }

  // Light track so the stars read on the dark stage.
  &__stars {
    --dh-rating-stars-track-color: rgb(255 255 255 / 35%);
  }

  // ── Collapsible description panel (shown above the bar) ────────
  &__details-panel {
    order: 1;
    max-height: 0;
    overflow: hidden;
    opacity: 0.8;
    transition: max-height 0.4s ease, padding-bottom 0.4s ease;

    &--open {
      max-height: 200px;
      padding-bottom: 16px;

      @include dh-sm {
        padding-bottom: 36px;
      }
    }
  }

  &__details-text {
    display: -webkit-box;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
  }
}
